import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { CalendarCheck, Clock3 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function HostHome() {
  const { formatDateTime } = useLocale();
  const { user, org } = useAuth();
  const [pre, setPre] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Phase 0: hosts see a read-only teaser — we'll surface audit entries for their own actions
        const { data } = await api.get("/orgs/me/audit", { params: { limit: 50 } });
        setPre(data.filter((r) => r.user_id === user?.id));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  return (
    <AppShell>
      <div className="eb-overline mb-2">Host</div>
      <h1 className="text-3xl font-semibold" data-testid="host-heading">Hi {user?.name?.split(" ")[0]}</h1>
      <p className="text-sm text-navy-500 mt-1">Your upcoming visitors and activity in {org?.name}.</p>

      <div className="mt-8 eb-card p-8 text-center" data-testid="host-empty">
        <CalendarCheck className="w-10 h-10 mx-auto text-navy-300 mb-3" />
        <h2 className="font-semibold">No pre-registrations yet</h2>
        <p className="text-sm text-navy-500 mt-1 max-w-md mx-auto">
          Ask your organisation admin to pre-register your next visitor. Once they sign in at reception, you'll be notified.
        </p>
      </div>

      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Clock3 className="w-4 h-4" /> Recent activity
        </h2>
        {loading ? (
          <p className="text-navy-500 text-sm">Loading…</p>
        ) : pre.length === 0 ? (
          <p className="text-navy-500 text-sm">No recent activity.</p>
        ) : (
          <div className="eb-card divide-y divide-navy-100">
            {pre.map((r) => (
              <div key={r.id} className="px-5 py-3 text-sm flex flex-wrap items-center gap-3" data-testid={`host-activity-${r.id}`}>
                <span className="font-medium">{r.action}</span>
                <span className="ml-auto text-navy-400 text-xs">{formatDateTime(r.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
